import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { isEmpty } from "lodash";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import fcLocaleFR from "@fullcalendar/core/locales/fr";
import moment from "moment";
import AgendaConfigForm from "../../../components/AgendaConfigForm";
import Modal from "../../../components/ModalUI";
import { userAtom } from "../../../state/auth";
import Spinner from "../../../components/Spinner";
import getCalendarOptions from "./calendarOptions";
import CalendarEvent from "./CalendarEvent";
import "./styles.scss";
import ICalendarEvent from "../../../types/ICalendarEvent";
import Icon from "../../../components/Icon";
import { getAgendaConfig } from "../../../api/medecin";
import { getAllMyLeaves } from "../../../api/medecin";
import { FaCalendarAlt, FaList } from "react-icons/fa";

import DayPlan from "./DayComp";
import { getAllScreeningEvents } from "../../../api/screeningEvent";
import CampagneForm from "./CampagneForm";
import { getAllClients } from "../../../api/client";
import ListCampagne from "./ListCampagne";

const getEventStatus = (event: any): "Planifiée" | "Non planifiée" => {
  const screeningEvent = event.extendedProps?.screeningEvent;

  const hasDocReq = !!screeningEvent?.docReq;
  const hasDocExpert = !!screeningEvent?.docExpert;

  return hasDocReq && hasDocExpert
    ? "Planifiée"
    : "Non planifiée";
};

const Calendar: React.FC = () => {
  const [showAgendaModal, setShowAgendaModal] = React.useState("") as any;
  const [agendaConfig, setAgendaConfig] = useState<any[]>([]);
  const [leaves, setLeaves] = useState([]) as any;
  const [eventsList, setEventsList] = React.useState<ICalendarEvent[]>([]);
  const [user] = useAtom(userAtom);
  const [nbravaibleDoctors, setNbrAvaibleDoctors] = useState(0);
  const [selectedDay, setSelectedDay] = useState(moment().format("dddd"));
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [weekConfig, setWeekConfig] = useState<any>();
  const [calendarTitle, setCalendarTitle] = useState("");
  const [currentView, setCurrentView] = useState<
    "dayGridMonth" | "timeGridWeek"
  >("timeGridWeek");
  const [clientList, setClientList] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<
    "ALL" | "PLANIFIEE" | "NON_PLANIFIEE"
  >("ALL");

  const fcRef = React.useRef<any>(null);
  const isResponsable = user?.type === "medecinResponsable" ? true : false;
  const now = moment();
  const calendarScrollToTime =
    now.hours() > 2
      ? now.subtract(90, "minutes").format("HH:mm:ss")
      : now.format("HH:mm:ss");

  const calendarOptions = getCalendarOptions(
    fcLocaleFR,
    isResponsable,
    nbravaibleDoctors,
    calendarScrollToTime,
    () => setShowAgendaModal("nouvelleCampagne"),
    () => setShowAgendaModal("gestionCampagnes"),
    () => setShowAgendaModal("nouvelleJournee"),
    );

  const filteredEvents = eventsList.filter((event) => {
    if (statusFilter === "ALL") return true;

    const status = getEventStatus(event);

    if (statusFilter === "PLANIFIEE") {
      return status === "Planifiée";
    }

    if (statusFilter === "NON_PLANIFIEE") {
      return status === "Non planifiée";
    }

    return true;
  });

  const refreshEvents = async () => {
    await loadScreeningEvents();
  };
  const loadScreeningEvents = async () => {
    try {
      const res = await getAllScreeningEvents();
      const events = Array.isArray(res) ? res : res.response || [];
      //console.log(res)
      //console.log("hello", res.response)
      // Map your API events to FullCalendar format
      const calendarEvents = events.map((event: any) => ({
        id: event._id,
        title: event.clientId?.name || "Client",
        start: event.eventDate,
        end: event.eventDate,
        extendedProps: {
          client: event.clientId,
          docReq: event.docReq,
          docExpert: event.docExpert,
          screeningEvent: event,
        },
      }));
      setEventsList(calendarEvents);
    } catch (error) {
      console.error("Failed to load screening events", error);
    }
  };

  React.useEffect(() => {
    //loadAvailabilities();
    setSelectedDay(moment().format("dddd"));
  }, [user]);

  useEffect(() => {
    loadScreeningEvents();
  }, []);

  const planifieeCount = eventsList.filter(
    (event) => getEventStatus(event) === "Planifiée"
  ).length;

  const nonPlanifieeCount = eventsList.filter(
    (event) => getEventStatus(event) === "Non planifiée"
  ).length;

  const totalCount = eventsList.length;

  useEffect(() => {
    async function fetchMyAPI() {
      const res = await getAllClients();
      const list = Array.isArray(res) ? res : res.response || res.data || [];
      const options = list.map((client: any) => ({
        label: client.name,
        value: client._id,
      }));
      setClientList(options);
    }

    fetchMyAPI();
  }, []);

  return (
    <section id="agenda" className="flex">
      <div className="w-full relative">
        <div className="flex items-center justify-between bg-white rounded-xl shadow-sm px-4 py-2 mb-4">
          {/* Left — Prev / Next */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => fcRef.current?.getApi().prev()}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition"
            >
              ‹
            </button>
            <button
              onClick={() => fcRef.current?.getApi().next()}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition"
            >
              ›
            </button>
          </div>

          <div className="flex justify-center gap-2">

            <button
              onClick={() => fcRef.current?.getApi().changeView("dayGridMonth")}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 transition"
            >
              <FaCalendarAlt />
              Calendrier
            </button>

            {/* List Button */}
            <button
              onClick={() => fcRef.current?.getApi().changeView("listMonth")}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 transition"
            >
              <FaList />
              Liste
            </button>

          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => fcRef.current?.getApi().changeView("timeGridWeek")}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 transition"
            >
              Semaine
            </button>
            <button
              onClick={() => fcRef.current?.getApi().changeView("dayGridMonth")}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 transition"
            >
              Mois
            </button>

            

            
            <div className="flex items-center gap-2">

              {/* Toutes */}
              <button
                onClick={() => setStatusFilter("ALL")}
                className={`
                  flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition
                  ${
                    statusFilter === "ALL"
                      ? "bg-gray-900 text-white shadow"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }
                `}
              >
                Toutes
                <span
                  className={`
                    text-xs px-2 py-0.5 rounded-full
                    ${
                      statusFilter === "ALL"
                        ? "bg-white text-gray-900"
                        : "bg-gray-300 text-gray-800"
                    }
                  `}
                >
                  {totalCount}
                </span>
              </button>

              {/* Planifiée */}
              <button
                onClick={() => setStatusFilter("PLANIFIEE")}
                className={`
                  flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition
                  ${
                    statusFilter === "PLANIFIEE"
                      ? "bg-green-600 text-white shadow"
                      : "bg-green-100 text-green-800 hover:bg-green-200"
                  }
                `}
              >
                Planifiée
                <span
                  className={`
                    text-xs px-2 py-0.5 rounded-full
                    ${
                      statusFilter === "PLANIFIEE"
                        ? "bg-white text-green-700"
                        : "bg-green-200 text-green-900"
                    }
                  `}
                >
                  {planifieeCount}
                </span>
              </button>

              {/* Non planifiée */}
              <button
                onClick={() => setStatusFilter("NON_PLANIFIEE")}
                className={`
                  flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition
                  ${
                    statusFilter === "NON_PLANIFIEE"
                      ? "bg-red-600 text-white shadow"
                      : "bg-red-100 text-red-800 hover:bg-red-200"
                  }
                `}
              >
                Non planifiée
                <span
                  className={`
                    text-xs px-2 py-0.5 rounded-full
                    ${
                      statusFilter === "NON_PLANIFIEE"
                        ? "bg-white text-red-700"
                        : "bg-red-200 text-red-900"
                    }
                  `}
                >
                  {nonPlanifieeCount}
                </span>
              </button>

            </div>
            
          </div>
        </div>
        <FullCalendar
          ref={fcRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
          themeSystem="bootstrap"
          displayEventTime={false}
          eventContent={CalendarEvent}
          events={filteredEvents}
          {...calendarOptions}
          eventClick={(info) => {
            setSelectedEvent(info.event.extendedProps.screeningEvent);
            setShowAgendaModal("editEvent");
          }}
          datesSet={(arg) => {
            setCalendarTitle(arg.view.title);

            const root = document.getElementById("agenda");
            if (!root) return;

            root.classList.toggle(
              "is-month-view",
              arg.view.type === "dayGridMonth"
            );
            root.classList.toggle(
              "is-week-view",
              arg.view.type === "timeGridWeek"
            );
          }}
        />
        {false && (
          <div className="absolute inset-0 bg-opacity-agenda">
            <Spinner />
          </div>
        )}
      </div>

      {showAgendaModal === "newEvent" ? (
        <Modal
          showModal={showAgendaModal.length > 0}
          slidemodal={!isResponsable}
          closeModal={() => {
            // When close modal reset component
            setShowAgendaModal("");
          }}
        >
          <div>
            <div className="icon-secondary cursor-pointer float-right mt-4 mr-2">
              <Icon
                isIcon={true}
                name="close"
                onClick={() => {
                  setShowAgendaModal("");
                }}
                style={{ marginTop: "6px" }}
              />
            </div>
            <AgendaConfigForm
              closeModal={() => {
                setShowAgendaModal("");
              }}
              agendaConfig={agendaConfig}
            />{" "}
          </div>
        </Modal>
      ) : showAgendaModal === "nouvelleJournee" ? (
        <>
          {showAgendaModal.length > 0 ? (
            <>
              <div
                className={`fixed inset-0 z-50 outline-none focus:outline-none  bg-opacity-modal  ${" flex justify-center items-center"}`}
              >
                <div
                  className={`relative w-2/4 h-full  my-6 ml-auto  ${"m-auto"}`}
                >
                  <div className=" h-full">
                    {" "}
                    <DayPlan
                      data={weekConfig}
                      setShowAgendaModal={setShowAgendaModal}
                      day={selectedDay}
                      onSuccess={refreshEvents}
                    />
                  </div>
                </div>
                {/* </div> */}
              </div>
              <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
            </>
          ) : null}
        </>
      ) : showAgendaModal === "nouvelleCampagne" ? <>
          {showAgendaModal.length > 0 ? (
            <>
              <div
                className={`fixed inset-0 z-50 outline-none focus:outline-none  bg-opacity-modal  ${" flex justify-center items-center"}`}
              >
                <div
                  className={`relative w-2/4 h-fit  my-6 ml-auto  ${"m-auto"}`}
                >
                  <div className="">
                    {" "}
                    <CampagneForm setShowAgendaModal={setShowAgendaModal} clientList={clientList} />
                  </div>
                </div>
                {/* </div> */}
              </div>
              <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
            </>
          ) : null}
        </>: showAgendaModal === "gestionCampagnes" ? <>
          {showAgendaModal.length > 0 ? (
            <>
              <div
                className={`fixed inset-0 z-50 outline-none focus:outline-none  bg-opacity-modal  ${" flex justify-center items-center"}`}
              >
                <div
                  className={`relative w-2/4 h-fit  my-6 ml-auto  ${"m-auto"}`}
                >
                  <div className="">
                    {" "}
                    <ListCampagne setShowAgendaModal={setShowAgendaModal} clientList={clientList} />
                  </div>
                </div>
                {/* </div> */}
              </div>
              <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
            </>
          ) : null}
        </>: null}

      {showAgendaModal === "editEvent" && selectedEvent && (
        <Modal
          showModal={true}
          closeModal={() => {
            setShowAgendaModal("");
            setSelectedEvent(null);
          }}
        >
          <DayPlan
            data={selectedEvent}
            setShowAgendaModal={setShowAgendaModal}
            day={moment(selectedEvent.eventDate).format("dddd")}
            onSuccess={refreshEvents}
          />
        </Modal>
      )}
    </section>
  );
};

export default Calendar;
